import { useKitbagAuth } from '@statsbomb/kitbag-auth';
import { Typography } from '@statsbomb/kitbag-components';
import { useId } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { CONTACT_SUPPORT } from '@/consts/links';
import { useLogout } from '@/hooks/useLogout';

export const LicenseError = ({ title, description }: { title: string; description: string }) => {
  const { t } = useTranslation('subscriptionError');
  const logout = useLogout();
  const { user } = useKitbagAuth();
  const accountLineId = useId();

  return (
    <div className="flex h-full isolate font-helvetica antialiased" data-testid="appshell">
      <div className="w-full h-full flex items-center justify-center bg-[#121212]">
        <div className="flex flex-col items-center justify-center m-auto px-6 py-10 gap-8 text-[#C3CEDB] bg-[#191F24] rounded-xl">
          <header>
            <h1 className="flex flex-col items-center justify-center text-3xl font-light leading-8 tracking-[-0.56px]">
              <span>Hudl Statsbomb</span>
              <span>{title}</span>
            </h1>
          </header>
          <div>
            <span className="text-left font-normal w-[330px] leading-6 block">{description}</span>
          </div>
          <footer className="flex flex-col items-center justify-center gap-4 w-full">
            <a
              className="flex items-center justify-center h-14 bg-[#027DBB] hover:bg-[#026CA1] text-sm text-[#027DBB] font-bold text-white w-full rounded-sm py-2"
              href={CONTACT_SUPPORT}
              target="_blank"
              rel="noreferrer"
            >
              {t('support')}
            </a>
            {user?.email && (
              // inverse ink: this panel is hard-coded dark while the app theme is light
              <Typography id={accountLineId} variant="bodyRegular" colour="inverse.main">
                {/* React escapes the text node, so i18next escaping would double-encode e.g. apostrophes */}
                {t('logged_in_as', { email: user.email, interpolation: { escapeValue: false } })}
              </Typography>
            )}
            <button
              type="button"
              className="flex items-center justify-center text-sm text-[#14B0FF] hover:bg-[#083045] font-bold rounded-sm py-1.5 px-4"
              onClick={logout}
              aria-describedby={user?.email ? accountLineId : undefined}
            >
              {t('log_out')}
            </button>
          </footer>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 w-full h-12 flex items-center justify-center text-[#C3CEDB] text-xs">
        <Trans i18nKey="disclaimer" ns="subscriptionError">
          By continuing, you agree to our
          <a
            className="text-[#4DB9EB] hover:text-[#75D3FF] mx-1"
            href="https://www.hudl.com/terms"
            target="_blank"
            rel="noreferrer"
          >
            Site Terms
          </a>
          and
          <a
            className="text-[#4DB9EB] hover:text-[#75D3FF] ml-1"
            href="https://www.hudl.com/privacy"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>
          .
        </Trans>
      </div>
    </div>
  );
};
